import { useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { JOIN_WINDOW_LEAD_MINUTES } from '@proctoring/shared';
import { TelemetryFeed } from '@proctoring/session-ui';
import {
  downloadTelemetryExport,
  useInterviewerInterview,
  useInterviewerTelemetry,
} from '../../api/queries.js';
import { useApi } from '../../auth/AuthProvider.js';
import { FullPageMessage } from '../../components/FullPageMessage.js';
import { StateBadge } from '../../components/StateBadge.js';
import { formatDateTime } from '../../components/format.js';
import { useJoinWindow } from '../candidate/useCountdown.js';

export function InterviewerInterviewPage(): ReactElement {
  const { interviewId = '' } = useParams();
  const api = useApi();
  const { data: interview, isPending, error } = useInterviewerInterview(interviewId);
  const window = useJoinWindow(interview ?? null);

  const finished = interview?.state === 'completed' || interview?.state === 'cancelled';
  const telemetry = useInterviewerTelemetry(interviewId, Boolean(interview));

  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'json' | 'csv' | null>(null);

  if (isPending) return <FullPageMessage title="Loading interview…" />;
  if (error) {
    return <FullPageMessage title="Could not load interview" detail={messageForError(error)} />;
  }

  async function onExport(format: 'json' | 'csv'): Promise<void> {
    setExportError(null);
    setExporting(format);
    try {
      await downloadTelemetryExport(api, interviewId, format, 'interviewer');
    } catch (cause) {
      setExportError(messageForError(cause));
    } finally {
      setExporting(null);
    }
  }

  const events = telemetry.data?.events ?? [];
  // The API returns oldest-first; the feed component expects newest-first.
  const ordered = [...events].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-10">
      <Link to="/interviewer" className="text-sm text-session-muted hover:text-session-text">
        ← All interviews
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-session-text">
            {interview.jobProfile.name} interview
          </h1>
          <p className="mt-1 text-sm text-session-muted">{formatDateTime(interview.scheduledAt)}</p>
        </div>
        <StateBadge state={interview.state} />
      </div>

      <section className="panel mt-8 p-5" aria-labelledby="participants-heading">
        <h2 id="participants-heading" className="text-sm font-semibold text-session-text">
          Participants
        </h2>
        <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <div>
            <dt className="field-label">Candidate</dt>
            <dd className="text-sm text-session-text">
              {interview.candidate.fullName}
              <span className="ml-2 text-session-dim">{interview.candidate.email}</span>
            </dd>
          </div>
          <div>
            <dt className="field-label">Interviewers</dt>
            <dd className="text-sm text-session-text">
              {interview.interviewers.map((person) => person.fullName).join(', ')}
            </dd>
          </div>
          <div>
            <dt className="field-label">Started</dt>
            <dd className="text-sm text-session-text">
              {interview.startedAt ? formatDateTime(interview.startedAt) : '—'}
            </dd>
          </div>
          <div>
            <dt className="field-label">Ended</dt>
            <dd className="text-sm text-session-text">
              {interview.endedAt ? formatDateTime(interview.endedAt) : '—'}
            </dd>
          </div>
        </dl>
      </section>

      {!finished ? (
        <section className="panel mt-6 p-5" aria-labelledby="join-heading">
          <h2 id="join-heading" className="text-sm font-semibold text-session-text">
            Session
          </h2>
          <p className="mt-2 text-sm text-session-muted">
            The room opens {JOIN_WINDOW_LEAD_MINUTES} minutes before the scheduled start and stays
            open until you end the interview.
          </p>
          <Link
            to={`/interviewer/interviews/${interviewId}/session`}
            data-testid="join-session-link"
            aria-disabled={window?.state !== 'open'}
            onClick={(event) => {
              if (window?.state !== 'open') event.preventDefault();
            }}
            className={`btn-primary mt-4 ${window?.state !== 'open' ? 'pointer-events-none opacity-50' : ''}`}
          >
            Join session
          </Link>
          {window?.state === 'waiting' ? (
            <p className="mt-3 text-xs text-session-dim">
              Opens at {formatDateTime(window.opensAt.toISOString())}.
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="mt-6" aria-labelledby="telemetry-heading">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 id="telemetry-heading" className="text-sm font-semibold text-session-text">
            Telemetry log
          </h2>
          <div className="flex gap-2">
            {(['json', 'csv'] as const).map((format) => (
              <button
                key={format}
                type="button"
                onClick={() => void onExport(format)}
                disabled={exporting !== null}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                {exporting === format ? 'Exporting…' : `Export ${format.toUpperCase()}`}
              </button>
            ))}
          </div>
        </div>

        {exportError ? (
          <p role="alert" className="form-error mb-3">
            {exportError}
          </p>
        ) : null}

        {telemetry.isPending ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">Loading telemetry…</p>
        ) : telemetry.error ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">
            {messageForError(telemetry.error)}
          </p>
        ) : (
          <TelemetryFeed
            events={ordered}
            emptyMessage="No events were recorded for this interview."
            className="panel max-h-[32rem] overflow-hidden"
          />
        )}
      </section>
    </div>
  );
}
